/// <reference types="react" />
/// <reference types="react-router-dom" />
/// <reference types="@tanstack/react-query" />
/// <reference types="react-hot-toast" />

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getAllBookings, updateBookingStatus, deleteBooking, updateExpiredBookingsStatus } from '../../services/supabase';
import { BookingStatus } from '../../types';
import toast from 'react-hot-toast';

// Интерфейс для пользователя
interface User {
    id: string;
    username?: string | null;
}

// Интерфейс для оборудования
interface Equipment {
    id: string;
    name: string;
    daily_rate: number;
    quantity: number;
    sort_order?: number | null;
}

// Интерфейс для элемента бронирования
interface BookingEquipment {
    id: string;
    booking_id: string;
    equipment_id: string;
    quantity: number;
    equipment?: Equipment;
}

// Интерфейс для бронирования
interface Booking {
    id: string;
    user_id: string;
    start_date: string;
    end_date: string;
    status: string | null;
    created_at: string | null;
    updated_at: string | null;
    booking_equipment: BookingEquipment[];
    users?: User | null; // Поле будет добавлено после получения данных
    project_name?: string | null;
}

const AdminBookingsList = () => {
    const [filterStatus, setFilterStatus] = useState<BookingStatus | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const queryClient = useQueryClient();
    const [autoUpdateChecked, setAutoUpdateChecked] = useState(false);
    const [lastAutoUpdateTime, setLastAutoUpdateTime] = useState<string | null>(null);

    // Получение всех бронирований
    const { data: bookings = [], isLoading, isError } = useQuery<Booking[]>({
        queryKey: ['admin', 'bookings-list'],
        queryFn: async () => {
            console.log('Получение списка бронирований для страницы AdminBookingsList');
            const { data, error } = await getAllBookings();
            if (error) {
                console.error('Ошибка при получении списка бронирований:', error);
                throw error;
            }
            console.log('Получены данные о бронированиях:', data ? data.length : 0, 'записей');
            return data as Booking[] || [];
        },
        staleTime: 0, // Отключаем кеширование
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });

    // Функция для проверки и обновления статуса просроченных бронирований
    const updateExpiredBookings = async () => {
        if (!bookings || bookings.length === 0 || autoUpdateChecked) return;

        console.log('Проверка просроченных бронирований...');
        const currentDate = new Date();
        const formattedTime = currentDate.toLocaleTimeString('ru-RU');
        currentDate.setHours(0, 0, 0, 0); // Устанавливаем время на начало дня

        let updatesPerformed = false;

        // Проверяем каждое бронирование
        for (const booking of bookings) {
            // Проверяем только бронирования со статусом "confirmed" (подтверждено)
            if (booking.status === 'confirmed') {
                const endDate = new Date(booking.end_date);
                endDate.setHours(23, 59, 59, 999); // Устанавливаем время на конец дня

                // Если дата окончания бронирования прошла, меняем статус на "completed"
                if (endDate < currentDate) {
                    console.log(`Бронирование ${booking.id} просрочено, обновляем статус на "завершено"`);
                    try {
                        await updateBookingStatus(booking.id, 'completed');
                        updatesPerformed = true;
                    } catch (error) {
                        console.error(`Ошибка при обновлении статуса бронирования ${booking.id}:`, error);
                    }
                }
            }
        }

        // Если были выполнены обновления, обновляем данные на странице
        if (updatesPerformed) {
            queryClient.invalidateQueries({ queryKey: ['admin', 'bookings-list'] });
            toast.success('Статусы просроченных бронирований обновлены');
        } else {
            toast.success('Просроченных бронирований не найдено');
        }

        setLastAutoUpdateTime(formattedTime);
        setAutoUpdateChecked(true); // Отмечаем, что проверка была выполнена
    };

    // Эффект для запуска автоматического обновления статусов при загрузке данных
    useEffect(() => {
        if (!isLoading && !isError && bookings && bookings.length > 0 && !autoUpdateChecked) {
            updateExpiredBookings();
        }
    }, [bookings, isLoading, isError, autoUpdateChecked]);

    // Мутация для обновления статуса бронирования
    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status }: { id: string; status: BookingStatus }) => {
            return updateBookingStatus(id, status);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'bookings-list'] });

            // Инвалидируем кеш доступного оборудования при изменении статуса,
            // особенно важно при установке статуса 'completed'
            queryClient.invalidateQueries({ queryKey: ['availableEquipment'] });
            queryClient.invalidateQueries({ queryKey: ['equipment'] });

            toast.success('Статус бронирования обновлен');
        },
        onError: (error: any) => {
            console.error('Ошибка при обновлении статуса:', error);
            toast.error('Не удалось обновить статус бронирования');
        }
    });

    // Мутация для удаления бронирования
    const deleteBookingMutation = useMutation({
        mutationFn: (id: string) => {
            return deleteBooking(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'bookings-list'] });
            toast.success('Бронирование удалено');
        },
        onError: (error: any) => {
            console.error('Ошибка при удалении бронирования:', error);
            toast.error('Не удалось удалить бронирование');
        }
    });

    // Мутация для обработки просроченных бронирований
    const updateExpiredMutation = useMutation({
        mutationFn: () => {
            return updateExpiredBookingsStatus();
        },
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({ queryKey: ['admin', 'bookings-list'] });
                toast.success(`Обновлено ${result.count} просроченных бронирований`);
                setLastAutoUpdateTime(new Date().toLocaleTimeString('ru-RU'));
            } else {
                toast.error('Ошибка при обновлении статусов');
            }
        },
        onError: (error: any) => {
            console.error('Ошибка при обновлении просроченных бронирований:', error);
            toast.error('Не удалось обновить статусы бронирований');
        }
    });

    // Обработчик изменения статуса
    const handleStatusChange = (bookingId: string, status: BookingStatus) => {
        // Проверяем, что мутация не выполняется в данный момент
        if (updateStatusMutation.isPending) {
            console.log('Мутация уже выполняется, игнорируем повторное нажатие');
            return;
        }

        updateStatusMutation.mutate({ id: bookingId, status });
    };

    // Обработчик удаления бронирования
    const handleDeleteBooking = (id: string) => {
        if (window.confirm('Вы уверены, что хотите удалить это бронирование?')) {
            deleteBookingMutation.mutate(id);
        }
    };

    // Получаем локализованное название статуса
    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'pending': return 'Ожидает';
            case 'confirmed': return 'Подтверждено';
            case 'completed': return 'Завершено';
            case 'cancelled': return 'Отменено';
            default: return status;
        }
    };

    // Получаем цвет для статуса
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'confirmed': return 'bg-green-100 text-green-800';
            case 'completed': return 'bg-blue-100 text-blue-800';
            case 'cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Фильтруем и ищем бронирования
    const filteredBookings = bookings.filter((booking: Booking) => {
        // Фильтр по статусу
        const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;

        // Приведение users к типу User для безопасного доступа к свойствам
        const userData = booking.users as User | null;

        // Поиск по ID, имени пользователя, и т.д.
        const userFullName = userData
            ? `${userData.username || 'Пользователь #' + booking.user_id.substring(0, 8)}`
            : '';

        const equipmentNames = booking.booking_equipment
            ? booking.booking_equipment
                .map((item: BookingEquipment) => item.equipment?.name || '')
                .join(' ')
            : '';

        const includesSearchTerm =
            booking.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            userFullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            equipmentNames.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesStatus && includesSearchTerm;
    });

    // Сортируем бронирования: 
    // 1. Незавершенные бронирования вверху, отсортированные по дате начала (ближайшие сверху)
    // 2. Завершенные бронирования внизу, также отсортированные по дате начала
    const sortedBookings = [...filteredBookings].sort((a, b) => {
        // Сначала сортируем по статусу
        // Ставим сверху "pending" и "confirmed", внизу "completed" и "cancelled"
        const statusOrder: Record<string, number> = {
            'pending': 0,
            'confirmed': 1,
            'completed': 3,
            'cancelled': 2
        };

        const statusA = statusOrder[a.status as keyof typeof statusOrder] ?? 4;
        const statusB = statusOrder[b.status as keyof typeof statusOrder] ?? 4;

        if (statusA !== statusB) {
            return statusA - statusB;
        }

        // Далее сортируем по дате начала (ближайшие даты сверху)
        const dateA = new Date(a.start_date).getTime();
        const dateB = new Date(b.start_date).getTime();

        return dateA - dateB;
    });

    // Безопасный форматировщик даты
    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return 'Нет данных';
        try {
            return new Date(dateString).toLocaleDateString('ru-RU');
        } catch (e) {
            return 'Неверная дата';
        }
    };

    // Отображаем индикатор загрузки
    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    // Отображаем сообщение об ошибке
    if (isError) {
        return (
            <div className="bg-red-50 p-4 rounded-md text-red-700 my-8">
                Произошла ошибка при загрузке бронирований. Пожалуйста, попробуйте позже.
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold text-gray-800">Управление бронированиями</h1>
                <Link to="/admin" className="text-primary-600 hover:underline">
                    ← Вернуться к панели управления
                </Link>
            </div>

            {/* Фильтры и поиск */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6 border border-gray-200">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-end">
                    <div className="flex flex-col md:flex-row gap-4 flex-1">
                        <div className="flex-1">
                            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Поиск</label>
                            <input
                                id="search"
                                type="text"
                                placeholder="Поиск по ID, имени клиента или оборудованию..."
                                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white text-gray-900"
                                style={{ backgroundColor: 'white !important', color: '#333 !important' }}
                                value={searchTerm}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="md:w-1/4">
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
                            <select
                                id="status"
                                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white text-gray-900"
                                style={{ backgroundColor: 'white !important', color: '#333 !important' }}
                                value={filterStatus}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                    setFilterStatus(e.target.value as BookingStatus | 'all')}
                            >
                                <option value="all">Все статусы</option>
                                <option value="pending">Ожидает</option>
                                <option value="confirmed">Подтверждено</option>
                                <option value="completed">Завершено</option>
                                <option value="cancelled">Отменено</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <button
                            type="button"
                            onClick={() => updateExpiredMutation.mutate()}
                            disabled={updateExpiredMutation.isPending}
                            className="px-4 py-2 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Обновить статусы просроченных бронирований"
                        >
                            {updateExpiredMutation.isPending
                                ? 'Обновление...'
                                : 'Обновить просроченные'}
                        </button>
                        {lastAutoUpdateTime && (
                            <div className="text-xs text-gray-500 mt-1 text-center">
                                Последняя проверка: {lastAutoUpdateTime}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Сообщение, если нет бронирований */}
            {filteredBookings.length === 0 && (
                <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
                    <p className="text-gray-500">Бронирования не найдены.</p>
                </div>
            )}

            {/* Таблица бронирований */}
            {filteredBookings.length > 0 && (
                <div className="mt-4 bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr className="divide-x divide-gray-200">
                                    <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                        ID
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                        Клиент
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                                        Даты
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                        Статус
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                                        Проект
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Оборудование
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                                        Действия
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedBookings.map((booking: Booking, index) => {
                                    // Приведение users к типу User для безопасной работы
                                    const userData = booking.users as User | null;
                                    return (
                                        <tr key={booking.id} className={`hover:bg-gray-50 divide-x divide-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {booking.id.substring(0, 8)}
                                            </td>
                                            <td className="px-2 py-4 whitespace-nowrap">
                                                {userData ? (
                                                    <div className="text-sm font-medium text-gray-900 truncate max-w-[100px]">
                                                        {userData.username || 'Пользователь #' + booking.user_id.substring(0, 8)}
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-gray-500">Нет данных</div>
                                                )}
                                            </td>
                                            <td className="px-2 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {formatDate(booking.start_date)}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    до {formatDate(booking.end_date)}
                                                </div>
                                            </td>
                                            <td className="px-2 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(booking.status as string)}`}>
                                                    {getStatusLabel(booking.status as string)}
                                                </span>
                                            </td>
                                            <td className="px-2 py-4 whitespace-nowrap">
                                                {booking.project_name ? (
                                                    <div className="text-sm text-gray-900 truncate max-w-xs">
                                                        {booking.project_name}
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-gray-500 italic">
                                                        Не указано
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-2 py-4">
                                                {booking.booking_equipment && booking.booking_equipment.length > 0 ? (
                                                    <div className="text-sm text-gray-900">
                                                        {booking.booking_equipment.slice(0, 2).map((item: BookingEquipment, index: number) => (
                                                            <div key={index} className="truncate max-w-xs">
                                                                {item.equipment?.name} ({item.quantity} шт.)
                                                            </div>
                                                        ))}
                                                        {booking.booking_equipment.length > 2 && (
                                                            <div className="text-xs text-gray-500">
                                                                +еще {booking.booking_equipment.length - 2}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-gray-500">Нет данных</div>
                                                )}
                                            </td>
                                            <td className="px-2 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end items-center space-x-1">
                                                    <Link
                                                        to={`/admin/bookings/${booking.id}`}
                                                        className="inline-flex items-center px-1.5 py-1 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                                    >
                                                        Детали
                                                    </Link>

                                                    <div className="border-l border-gray-200 pl-1 flex flex-wrap items-center gap-1 justify-end">
                                                        <div className="flex flex-wrap gap-1">
                                                            <button
                                                                onClick={() => handleStatusChange(booking.id, 'pending')}
                                                                disabled={booking.status === 'pending' || updateStatusMutation.isPending}
                                                                className={`px-1 py-0.5 rounded-md text-xs ${booking.status === 'pending'
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : updateStatusMutation.isPending && updateStatusMutation.variables?.id === booking.id
                                                                        ? 'bg-yellow-100 text-yellow-800 opacity-70 cursor-not-allowed'
                                                                        : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                                                    }`}
                                                            >
                                                                {updateStatusMutation.isPending
                                                                    && updateStatusMutation.variables?.id === booking.id
                                                                    && updateStatusMutation.variables?.status === 'pending'
                                                                    ? '...'
                                                                    : 'Ожидает'}
                                                            </button>
                                                            <button
                                                                onClick={() => handleStatusChange(booking.id, 'confirmed')}
                                                                disabled={booking.status === 'confirmed' || updateStatusMutation.isPending}
                                                                className={`px-1 py-0.5 rounded-md text-xs ${booking.status === 'confirmed'
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : updateStatusMutation.isPending && updateStatusMutation.variables?.id === booking.id
                                                                        ? 'bg-green-100 text-green-800 opacity-70 cursor-not-allowed'
                                                                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                                                                    }`}
                                                            >
                                                                {updateStatusMutation.isPending
                                                                    && updateStatusMutation.variables?.id === booking.id
                                                                    && updateStatusMutation.variables?.status === 'confirmed'
                                                                    ? '...'
                                                                    : 'Подтв.'}
                                                            </button>
                                                            <button
                                                                onClick={() => handleStatusChange(booking.id, 'completed')}
                                                                disabled={booking.status === 'completed' || updateStatusMutation.isPending}
                                                                className={`px-1 py-0.5 rounded-md text-xs ${booking.status === 'completed'
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : updateStatusMutation.isPending && updateStatusMutation.variables?.id === booking.id
                                                                        ? 'bg-blue-100 text-blue-800 opacity-70 cursor-not-allowed'
                                                                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                                                    }`}
                                                            >
                                                                {updateStatusMutation.isPending
                                                                    && updateStatusMutation.variables?.id === booking.id
                                                                    && updateStatusMutation.variables?.status === 'completed'
                                                                    ? '...'
                                                                    : 'Заверш.'}
                                                            </button>
                                                            <button
                                                                onClick={() => handleStatusChange(booking.id, 'cancelled')}
                                                                disabled={booking.status === 'cancelled' || updateStatusMutation.isPending}
                                                                className={`px-1 py-0.5 rounded-md text-xs ${booking.status === 'cancelled'
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : updateStatusMutation.isPending && updateStatusMutation.variables?.id === booking.id
                                                                        ? 'bg-red-100 text-red-800 opacity-70 cursor-not-allowed'
                                                                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                                                                    }`}
                                                            >
                                                                {updateStatusMutation.isPending
                                                                    && updateStatusMutation.variables?.id === booking.id
                                                                    && updateStatusMutation.variables?.status === 'cancelled'
                                                                    ? '...'
                                                                    : 'Отмена'}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="border-l border-gray-200 pl-1">
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center px-1.5 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                            onClick={() => handleDeleteBooking(booking.id)}
                                                        >
                                                            Удалить
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBookingsList; 